import React, { useState, useEffect, useContext } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { API_URL } from "../../components/api";
import { formatDateTime } from "../../../utils/dateFormat";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridFooterContainer,
  GridPagination,
} from "@mui/x-data-grid";
import ReceiptEdit from "./ReceiptEdit";
import AuthContext from "../../context/AuthContext";
import ExcelSVG from "./ExcelSVG.svg";
import FilterSVG from "./FilterSVG.svg";
import * as XLSX from "xlsx";
import NewReceiptPrint from "./NewReceiptPrint";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import "./ReceiptsTable.css";
// For API Requests
import axios from "axios";

const accountingNumberFormatter = new Intl.NumberFormat(
  "en-US",
  {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }
);

const formatAccountingNumber = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "0.00";
  }

  return accountingNumberFormatter.format(amount);
};

function ReceiptTable() {
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);

  const [showo, setShowo] = useState(false);
  const [dashoo, setDasho] = useState(false);
  const [print, setPrint] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([
    { field: "receipt_id", sort: "desc" },
  ]);
  const [receiptTotals, setReceiptTotals] = useState({
    paid_amount_usd: 0,
    paid_amount_lbp: 0,
    returned_usd: 0,
    returned_lbp: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [reason, setReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  const [printReceipt, setPrintReceipt] = useState(false);
  const handlePrintReceipt = () => setPrintReceipt(true);
  const handleClosePrintReceipt = () => setPrintReceipt(false);

  const { user } = useContext(AuthContext);
  let authTokens = JSON.parse(localStorage.getItem("authTokens"));

  const [filters, setFilters] = useState({
    receipt_id: "",
    fullname_en: "",
    fullname_dealer: "",
    paid_amount_usd: "",
    paid_amount_lbp: "",
    exchange_rate: "",
    discount_lbp: "",
    discount_usd: "",
    returned_lbp: "",
    returned_usd: "",
    initiator: "",
    date: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  const [searchInput, setSearchInput] = useState(""); // To capture search input

  // NEW: State to track column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

  const [ClientDetails, setClientDetails] = useState({
    client_id: 0,
    receipt_id: 0,
    paid_amount_usd: 0,
    paid_amount_lbp: 0,
    exchange_rate: "",
    discount_lbp: "",
    discount_usd: "",
    returned_lbp: "",
    returned_usd: "",
    fullname_en: "",
  });

  const updateClientDetails = (details) => {
    setClientDetails(details);
  };

  const buildReceiptParams = ({
    requestedPage = page + 1,
    requestedPageSize = pageSize,
  } = {}) => ({
    username: user.username,
    page: requestedPage,
    page_size: requestedPageSize,
    search: searchQuery,
    filters: JSON.stringify(filters),
    grid_filters: JSON.stringify(
      gridFilterModel.items || []
    ),
    sort_field:
      sortModel.length > 0
        ? sortModel[0].field
        : "receipt_id",
    sort_direction:
      sortModel.length > 0
        ? sortModel[0].sort
        : "desc",
  });

  const fetchData = async () => {
    try {
      const authTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.get(
        `${API_URL}/inventory/receipts/`,
        {
          headers: {
            Authorization:
              `Bearer ${authTokens.access}`,
          },
          params: buildReceiptParams(),
        }
      );

      if (response.status === 200) {
        setData(response.data.results || []);

        setRowCount(
          Number(
            response.data.pagination?.total_rows || 0
          )
        );

        setReceiptTotals(
          response.data.totals || {
            paid_amount_usd: 0,
            paid_amount_lbp: 0,
            returned_usd: 0,
            returned_lbp: 0,
          }
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    user.username,
    page,
    pageSize,
    searchQuery,
    filters,
    gridFilterModel,
    sortModel,
  ]);

  const columns = [
    { field: "receipt_id", headerName: "ID", flex: 2, minWidth: 80 },
    { field: "fullname_en", headerName: "Client Name", flex: 2, minWidth: 200 },
    { field: "fullname_dealer", headerName: "Dealer", flex: 2, minWidth: 100 },
    {
      field: "paid_amount_usd",
      headerName: "Paid USD",
      flex: 2,
      minWidth: 100,
    },
    {
      field: "paid_amount_lbp",
      headerName: "Paid LBP",
      flex: 2,
      minWidth: 100,
      valueFormatter: (params) =>
        formatAccountingNumber(params.value),
    },
    {
      field: "exchange_rate",
      headerName: "Rate",
      flex: 2,
      minWidth: 100,
      valueFormatter: (params) =>
        formatAccountingNumber(params.value),
    },
    {
      field: "balance_record",
      headerName: "Balance Record",
      flex: 2,
      minWidth: 120,
    },
    {
      field: "discount_usd",
      headerName: "Discount USD",
      flex: 2,
      minWidth: 100,
    },
    {
      field: "discount_lbp",
      headerName: "Discount LBP",
      flex: 2,
      minWidth: 100,
    },
    {
      field: "returned_lbp",
      headerName: "Returned LBP",
      flex: 2,
      minWidth: 100,
    },
    {
      field: "returned_usd",
      headerName: "Returned USD",
      flex: 2,
      minWidth: 100,
    },
    { field: "initiator", headerName: "Cashier", flex: 2, minWidth: 100 },
    {
      field: "date",
      headerName: "Date",
      minWidth: 200,
      sortable: true, // ensures it's sortable
      sort: "desc", // default descending sort
      valueFormatter: (params) => {
        const date = new Date(params.value);
        date.setHours(date.getHours() + 2);
        return formatDateTime(date);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 500,
      renderCell: (params) => (
        <div className="receipt-row-actions">
          <Button
            className="receipt-row-action"
            onClick={() =>
              handleOpenModal(params.row.receipt_id)
            }
          >
            Cancel
          </Button>

          <Button
            className="receipt-row-action"
            onClick={() => {
              const details = {
                receipt_id: params.row.receipt_id,
              };
              updateClientDetails(details);
              handlePrintReceipt();
            }}
          >
            Print
          </Button>

          <Button
            className="receipt-row-action"
            onClick={() =>
              handleOpenModalRefund(
                params.row.receipt_id
              )
            }
          >
            Refund
          </Button>

          <Button
            className="receipt-row-action"
            onClick={() => {
              const details = {
                receipt_id: params.row.receipt_id,
                client_id: params.row.client_id,
                paid_amount_usd:
                  params.row.paid_amount_usd,
                paid_amount_lbp:
                  params.row.paid_amount_lbp,
                exchange_rate:
                  params.row.exchange_rate,
                discount_lbp:
                  params.row.discount_lbp,
                returned_lbp:
                  params.row.returned_lbp,
                discount_usd:
                  params.row.discount_usd,
                returned_usd:
                  params.row.returned_usd,
                fullname_en: params.row.fullname_en,
              };
              updateClientDetails(details);
              handleShow();
            }}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];

  const getRowId = (row) => row.receipt_id;

  // Create a delayed search filter function
  const delaySearchFilter = (input) => {
    setPage(0);
    setSearchQuery(input);
  };

  useEffect(() => {
    // Save "searchValue" value before clearing other session storage items
    const searchValue = sessionStorage.getItem("searchReceipt");

    // Clear all session storage items except "searchValue"
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchReceipt") {
        sessionStorage.removeItem(key);
      }
    });

    // Restore "searchValue" value if needed
    if (searchValue) {
      sessionStorage.setItem("searchReceipt", searchValue);
      setSearchQuery(searchValue);
    }
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchInput(query);
    sessionStorage.setItem("searchReceipt", query);

    if (searchFilterTimeout) {
      clearTimeout(searchFilterTimeout);
    }

    const newTimeout = setTimeout(() => {
      delaySearchFilter(query);
    }, 100);
    setSearchFilterTimeout(newTimeout);
  };

  const handleOpenModal = (receiptId) => {
    setSelectedReceiptId(receiptId);
    setReason("");
    setCancelError("");
    setShowo(true);
  };

  const handleCloseModal = () => {
    setShowo(false);
    setSelectedReceiptId(null);
    setReason("");
    setCancelError("");
  };

  const handleOpenModalRefund = (receiptId) => {
    setSelectedReceiptId(receiptId);
    setDasho(true);
  };

  const handleCloseModalRefund = () => {
    setDasho(false);
    setSelectedReceiptId(null);
  };

  const handleConfirm = async () => {
    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      setCancelError(
        "Please enter a cancellation reason."
      );
      return;
    }

    setCancelError("");
    setIsCancelling(true);

    try {
      await axios.post(
        `${API_URL}/inventory/receipt/cancel/`,
        {
          receipt_id: selectedReceiptId,
          reason: trimmedReason,
        },
        {
          headers: {
            Authorization:
              `Bearer ${authTokens.access}`,
          },
        }
      );

      handleCloseModal();
      await fetchData();
    } catch (error) {
      console.error(
        "Error cancelling receipt:",
        error
      );

      setCancelError(
        error.response?.data?.message ||
          "Unable to cancel the receipt."
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handleConfirmRefund = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/inventory/receipt/refund/`,
        { receipt_id: selectedReceiptId, username: user.username },
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        }
      );

      if (response.status === 200) {
        console.log("Receipt:", response.data);
      }
    } catch (error) {
      console.error("Error confirming receipt:", error);
    } finally {
      handleCloseModalRefund();
      fetchData();
    }
  };

  // RECEIPTS_ALL_MATCHING_EXPORT
  // Search and filters are now applied by the backend.
  const filteredRows = data;

  const handleFilterChange = (field, value) => {
    setPage(0);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleGridFilterModelChange = (newModel) => {
    setPage(0);
    setGridFilterModel(newModel);
  };

  const handleSortModelChange = (newModel) => {
    setPage(0);
    setSortModel(newModel);
  };

  const columnHeaders = {
    fullname_en: "Full Name",
    fullname_dealer: "Dealer",
    paid_amount_usd: "Paid USD",
    paid_amount_lbp: "Paid LBP",
    exchange_rate: "Rate",
    balance_record: "Balance Record",
    discount_usd: "Discount USD",
    discount_lbp: "Discount LBP",
    returned_lbp: "Returned LBP",
    returned_usd: "Returned USD",
    initiator: "Cashier",
    date: "Date",
  };

  const gridExportColumnHeaders = {
    receipt_id: "ID",
    fullname_en: "Client Name",
    fullname_dealer: "Dealer",
    paid_amount_usd: "Paid USD",
    paid_amount_lbp: "Paid LBP",
    exchange_rate: "Rate",
    balance_record: "Balance Record",
    discount_usd: "Discount USD",
    discount_lbp: "Discount LBP",
    returned_lbp: "Returned LBP",
    returned_usd: "Returned USD",
    initiator: "Cashier",
    date: "Date",
  };

  const formatDateForExport = (dateValue) =>
    formatDateTime(dateValue);

  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(columnHeaders).forEach((field) => {
        if (columnVisibilityModel[field] !== false) {
          const headerName = columnHeaders[field];

          if (field === "date") {
            transformedRow[headerName] =
              formatDateForExport(row[field]);
          } else {
            transformedRow[headerName] =
              row[field] ?? "";
          }
        }
      });

      return transformedRow;
    });
  };

  const transformRowsForGridExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(gridExportColumnHeaders).forEach(
        (field) => {
          if (columnVisibilityModel[field] === false) {
            return;
          }

          const headerName =
            gridExportColumnHeaders[field];
          const value = row[field];

          if (field === "date" && value) {
            const date = new Date(value);
            date.setHours(date.getHours() + 2);
            transformedRow[headerName] =
              formatDateTime(date);
          } else if (
            field === "paid_amount_lbp" ||
            field === "exchange_rate"
          ) {
            transformedRow[headerName] =
              formatAccountingNumber(value);
          } else {
            transformedRow[headerName] =
              value ?? "";
          }
        }
      );

      return transformedRow;
    });
  };

  const fetchAllMatchingRows = async () => {
    const authTokens = JSON.parse(
      localStorage.getItem("authTokens")
    );

    const rows = [];
    let requestedPage = 1;
    let totalPages = 1;

    do {
      const response = await axios.get(
        `${API_URL}/inventory/receipts/`,
        {
          headers: {
            Authorization:
              `Bearer ${authTokens.access}`,
          },
          params: buildReceiptParams({
            requestedPage,
            requestedPageSize: 1000,
          }),
        }
      );

      const pageRows = response.data.results || [];
      rows.push(...pageRows);

      totalPages = Math.max(
        Number(
          response.data.pagination?.total_pages || 0
        ),
        1
      );

      requestedPage += 1;
    } while (requestedPage <= totalPages);

    return rows;
  };

  const [isPressed, setIsPressed] = useState(false);
  const [isExporting, setIsExporting] =
    useState(false);
  const handleExportToExcel = async () => {
    const rows = await fetchAllMatchingRows();
    const transformedRows =
      transformRowsForExport(rows);

    const worksheet =
      XLSX.utils.json_to_sheet(transformedRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Receipts"
    );

    XLSX.writeFile(workbook, "Receipts.xlsx");
  };

  const handleExportClick = async () => {
    setIsPressed(true);
    setIsExporting(true);

    try {
      await handleExportToExcel();
    } catch (error) {
      console.error(
        "Unable to export receipts to Excel:",
        error
      );
    } finally {
      setIsExporting(false);
      setIsPressed(false);
    }
  };

  const handleExportCsv = async () => {
    setIsExporting(true);

    try {
      const rows = await fetchAllMatchingRows();
      const transformedRows =
        transformRowsForGridExport(rows);

      const worksheet =
        XLSX.utils.json_to_sheet(transformedRows);
      const csv =
        "\ufeff" + XLSX.utils.sheet_to_csv(worksheet);
      const blob = new Blob(
        [csv],
        { type: "text/csv;charset=utf-8;" }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = "Receipts.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        "Unable to export receipts to CSV:",
        error
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handlePrintAll = async () => {

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      console.error("The print window was blocked.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(
      "<!doctype html><html><head>" +
      "<title>Receipts</title></head>" +
      "<body><p>Preparing receipts...</p></body></html>"
    );
    printWindow.document.close();

    setIsExporting(true);

    try {
      const rows = await fetchAllMatchingRows();
      const transformedRows =
        transformRowsForGridExport(rows);

      const visibleHeaders = Object.entries(
        gridExportColumnHeaders
      )
        .filter(
          ([field]) =>
            columnVisibilityModel[field] !== false
        )
        .map(([, header]) => header);

      const documentObject = printWindow.document;
      documentObject.body.textContent = "";

      const style = documentObject.createElement(
        "style"
      );
      style.textContent = `
        @page {
          size: landscape;
          margin: 10mm;
        }

        body {
          color: #000;
          font-family: Arial, sans-serif;
          font-size: 10px;
        }

        h1 {
          font-size: 18px;
          margin: 0 0 12px;
        }

        table {
          border-collapse: collapse;
          width: 100%;
        }

        th,
        td {
          border: 1px solid #777;
          padding: 4px;
          text-align: left;
          vertical-align: top;
        }

        th {
          background: #e8edf0;
          font-weight: 700;
        }
      `;
      documentObject.head.appendChild(style);

      const heading = documentObject.createElement(
        "h1"
      );
      heading.textContent = "Receipts";
      documentObject.body.appendChild(heading);

      const table = documentObject.createElement(
        "table"
      );
      const tableHead = documentObject.createElement(
        "thead"
      );
      const headerRow = documentObject.createElement(
        "tr"
      );

      visibleHeaders.forEach((header) => {
        const cell = documentObject.createElement(
          "th"
        );
        cell.textContent = header;
        headerRow.appendChild(cell);
      });

      tableHead.appendChild(headerRow);
      table.appendChild(tableHead);

      const tableBody = documentObject.createElement(
        "tbody"
      );

      transformedRows.forEach((row) => {
        const tableRow =
          documentObject.createElement("tr");

        visibleHeaders.forEach((header) => {
          const cell =
            documentObject.createElement("td");
          cell.textContent = String(
            row[header] ?? ""
          );
          tableRow.appendChild(cell);
        });

        tableBody.appendChild(tableRow);
      });

      table.appendChild(tableBody);
      documentObject.body.appendChild(table);

      window.setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error(
        "Unable to prepare receipts for printing:",
        error
      );
      printWindow.close();
    } finally {
      setIsExporting(false);
    }
  };

  const ReceiptsCsvExportMenuItem = ({
    hideMenu,
  }) => (
    <MenuItem
      onClick={() => {
        hideMenu();
        handleExportCsv();
      }}
    >
      Download as CSV
    </MenuItem>
  );

  const ReceiptsPrintExportMenuItem = ({
    hideMenu,
  }) => (
    <MenuItem
      onClick={() => {
        hideMenu();
        handlePrintAll();
      }}
    >
      Print
    </MenuItem>
  );

  const sumPaidUsd = Number(
    receiptTotals.paid_amount_usd || 0
  );
  const sumPaidLbp = Number(
    receiptTotals.paid_amount_lbp || 0
  );
  const sumReturnedUsd = Number(
    receiptTotals.returned_usd || 0
  );
  const sumReturnedLbp = Number(
    receiptTotals.returned_lbp || 0
  );

  const CustomToolbar = () => (
    <GridToolbarContainer className="receipts-toolbar">
      <GridToolbarColumnsButton
        className="receipts-toolbar-button"
      />

      <Button
        onClick={() => setShowFilterModal(true)}
        className="receipts-toolbar-button"
      >
        <img
          src={FilterSVG}
          alt=""
          className="receipts-toolbar-icon"
        />
        Filter
      </Button>

      <GridToolbarExportContainer
        className="receipts-toolbar-button receipts-export-button"
        disabled={isExporting}
      >
        <ReceiptsCsvExportMenuItem />
        <ReceiptsPrintExportMenuItem />
      </GridToolbarExportContainer>

      <Button
        onClick={handleExportClick}
        disabled={isExporting}
        className={`receipts-toolbar-button ${
          isPressed ? "is-pressed" : ""
        }`}
      >
        <img
          src={ExcelSVG}
          alt=""
          className="receipts-toolbar-icon"
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "10px",
          color: "white",
        }}
      >
        <div>Paid USD: {sumPaidUsd.toFixed(2)}</div>
        <div>
          Paid LBP: {formatAccountingNumber(sumPaidLbp)}
        </div>
        <div>Returned USD: {sumReturnedUsd.toFixed(2)}</div>
        <div>Returned LBP: {sumReturnedLbp.toFixed(2)}</div>
        <GridPagination />
      </div>
    </GridFooterContainer>
  );

  return (
    <div className="ClientTable accounting-receipts-table">
      <style>{`
        html body .MuiPopover-root
          .MuiPaper-root.MuiPopover-paper {
          position: absolute !important;
        }

        .MuiDataGrid-panelWrapper {
          background: #122632 !important;
        }
      `}</style>
      <input
        type="text"
        placeholder="Search..."
        value={searchInput}
        onChange={handleSearchChange}
        className="search-client"
      />
      {!loading && (
        <div style={{ height: 520, width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            paginationMode="server"
            filterMode="server"
            sortingMode="server"
            onPageChange={(newPage) =>
              setPage(newPage)
            }
            onPageSizeChange={(newPageSize) => {
              setPage(0);
              setPageSize(newPageSize);
            }}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterModel={gridFilterModel}
            onFilterModelChange={
              handleGridFilterModelChange
            }
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            components={{
              Toolbar: CustomToolbar,
              Footer: CustomFooter,
            }}
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
          />
        </div>
      )}
      <Modal
        show={showo}
        onHide={handleCloseModal}
        centered
        className="receipt-cancel-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Cancel Receipt
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="receipt-cancel-copy">
            Cancel this receipt and reverse its
            collection? Please provide a reason.
          </p>

          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason"
            placeholder="Enter the cancellation reason"
            variant="outlined"
            value={reason}
            error={Boolean(cancelError)}
            helperText={cancelError}
            disabled={isCancelling}
            className="receipt-cancel-reason"
            onChange={(event) => {
              setReason(event.target.value);

              if (cancelError) {
                setCancelError("");
              }
            }}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            className="receipt-cancel-secondary"
            onClick={handleCloseModal}
            disabled={isCancelling}
          >
            Keep Receipt
          </Button>

          <Button
            type="button"
            className="receipt-cancel-primary"
            onClick={handleConfirm}
            disabled={isCancelling}
          >
            {isCancelling
              ? "Cancelling..."
              : "Cancel Receipt"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={printReceipt} onHide={handleClosePrintReceipt}>
        <Modal.Header closeButton>
          <Modal.Title>Print</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NewReceiptPrint receipt_id={ClientDetails.receipt_id} />
        </Modal.Body>
      </Modal>
      <Modal show={dashoo} onHide={handleCloseModalRefund}>
        <Modal.Header closeButton>
          <Modal.Title>Refund Receipt</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to refund this receipt?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModalRefund}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRefund}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={show} onHide={handleClose} className="modal-lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Receipt</Modal.Title>
        </Modal.Header>
        <div className="forma">
          <ReceiptEdit
            receiptId={ClientDetails.receipt_id}
            client_id={ClientDetails.client_id}
            paid_amount_usd={ClientDetails.paid_amount_usd}
            paid_amount_lbp={ClientDetails.paid_amount_lbp}
            exchange_rate={ClientDetails.exchange_rate}
            discount_lbp={ClientDetails.discount_lbp}
            returned_lbp={ClientDetails.returned_lbp}
            returned_usd={ClientDetails.returned_usd}
            discount_usd={ClientDetails.discount_usd}
            fullname_en={ClientDetails.fullname_en}
          />
        </div>
      </Modal>
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {columns
            .filter((col) => col.field !== "actions")
            .map((col) => (
              <div key={col.field} className="filter">
                <label>{col.headerName}</label>
                <input
                  type="text"
                  placeholder={`Filter by ${col.headerName}`}
                  value={filters[col.field] || ""}
                  onChange={(e) =>
                    handleFilterChange(col.field, e.target.value)
                  }
                  className="filter-input"
                />
              </div>
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ReceiptTable;
